import moment from "moment";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import Common from "us.common";
import { $Collapse, $Drawer, $Skeleton } from "us.common/components";
import { BoxIcons, IconTypes } from "us.icons";
import { RootState } from "us.helper/types";
import { connect, ConnectedProps } from "react-redux";
import * as Actions from "us.collection.routines-and-activities/actions";
import {
  WASH_RESULT_DRAWER_DEFAULT,
  WashResultDrawer,
  WashResultsType,
} from "../../Constants";
import { useTranslation } from "react-i18next";
import { useLocation, useHistory, useRouteMatch } from "react-router-dom";
import { Note } from "us.collection.routines-and-activities/components/Note";
import { WashResultItem } from "us.collection.routines-and-activities/constants/History";

const { $Popover, $Button, $DateTimeLabel } = Common.Components;
type WashResultsProps = {
  debtorId: string;
};

/**
 * @description
 * WashResults Component
 *
 * Displays wash results grouped by wash type using a collapsible UI.
 * Allows users to preview JSON data via popover and view detailed notes in a drawer.
 *
 * Features:
 * - Fetch wash results on mount
 * - Group results by WashResultsType
 * - Show skeleton while loading
 * - Preview parsed JSON via popover
 * - Open selected item in Note drawer
 * @link Task- https://unicorn-solutions.atlassian.net/browse/USBC-4204
 * @author Keshan Nimnaka <keshana@unicorn-solutions.com>
 * @since 18/03/2026
 */
const WashResults: React.FC<WashResultsProps & PropsFromRedux> = (props) => {
  const { t } = useTranslation();
  const { getWashResults, washResults, isLoading, debtorId } = props;

  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();

  const [drawerInfo, setDrawerInfo] = useState<WashResultDrawer>(
    WASH_RESULT_DRAWER_DEFAULT,
  );

  useEffect(() => {
    if (getWashResults && debtorId != null && debtorId !== "") {
      getWashResults({ debtorId });
    }
  }, [debtorId]);

  const parseWashResult = (value: string) => {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  };

  const groupWashResults = (washResults: WashResultItem[]) => {
    return Object.values(WashResultsType).reduce(
      (grouped: Record<string, WashResultItem[]>, type) => {
        grouped[type] = washResults?.filter((x) => x.washType === type) || [];
        return grouped;
      },
      {} as Record<string, WashResultItem[]>,
    );
  };

  const washResultsByType = useMemo(() => {
    return groupWashResults(washResults || []);
  }, [washResults]);

  const onViewWashItem = (data: WashResultItem) => {
    const { washType, washResultObj, washResultId, createdBy } = data;

    const updatedData = {
      name: washResultObj,
      noteType: washType,
      noteId: washResultId,
      notified: true,
      source: createdBy,
    };

    setDrawerInfo({
      title: t("US.COLLECTION.ROUTINESANDACTIVITIES:HISTORY.NOTE"),
      isVisible: true,
      data: updatedData,
    });
  };

  const closeDrawer = () => {
    setDrawerInfo(WASH_RESULT_DRAWER_DEFAULT);
  };

  return (
    <>
      <Fragment>
        {isLoading ? (
          <$Skeleton active paragraph={{ rows: 5 }} />
        ) : (
          <$Collapse
            bordered={false}
            defaultActiveKey={Object.keys(washResultsByType)}
            className="cl-list-collapse"
          >
            {Object.keys(washResultsByType).map((washType) => (
              <$Collapse.Panel header={`${washType}`} key={washType}>
                {washResultsByType[washType].map((item, index) => (
                  <div key={index} className="cl-item case-log">
                    <div className="cl-actions">
                      <$Popover
                        overlayClassName="Wash Result"
                        placement="right"
                        title={t(
                          "US.COLLECTION.ROUTINESANDACTIVITIES:HISTORY.NOTE",
                        )}
                        content={
                          <pre style={{ maxWidth: 400 }}>
                            {JSON.stringify(
                              parseWashResult(item.washResultObj),
                              null,
                              2,
                            )}
                          </pre>
                        }
                        trigger="hover"
                      >
                        <$Button
                          type="link"
                          size="small"
                          className="row-icon row-icon-note"
                        >
                          <BoxIcons
                            type={IconTypes.BOX_ICON}
                            name="case-note"
                          />
                        </$Button>
                      </$Popover>
                    </div>

                    <div className="cl-details">
                      <span className="cl-title">
                        <a
                          onClick={() => onViewWashItem(item)}
                          data-testid="dashboard-history-noteOpen"
                        >
                          {item.washResultObj}
                        </a>
                      </span>

                      <span className="cl-desc">
                        {moment.utc(item?.createdDate).isValid() &&
                          moment
                            .utc(item?.createdDate)
                            .isAfter(moment.utc("0001-01-01")) && (
                            <$DateTimeLabel value={item?.createdDate} />
                          )}

                        {item?.createdBy && ` | ${item?.createdBy}`}
                      </span>
                    </div>
                  </div>
                ))}
              </$Collapse.Panel>
            ))}
          </$Collapse>
        )}
      </Fragment>

      <$Drawer
        title={drawerInfo.title}
        width={"75%"}
        visible={drawerInfo.isVisible}
        onClose={closeDrawer}
        destroyOnClose
        placement="left"
        className={"history-doc-viwer"}
      >
        <Note
          close={closeDrawer}
          data={drawerInfo.data}
          location={location}
          history={history}
          match={match}
        />
      </$Drawer>
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  const { logHistory } = state;
  const { washResults } = logHistory;

  return {
    washResults: washResults?.data || [],
    isLoading: washResults?.isLoading,
  };
};

const { historyAction } = Actions;
const { washResults } = historyAction;

const mapDispatchToProps = {
  getWashResults: washResults.get,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(WashResults);
